import apple_pay_mark from "assets/apple-pay-mark.svg";
import google_pay_mark from "assets/google-pay-mark.png";
import paypal_mark from "assets/paypal-mark.png";
import FundingOption from "constants/FundingOption";
import {
  COMMON_NAMESPACE,
  PAYABLE_REVIEW_NAMESPACE,
} from "constants/i18n-namespaces";
import { useCurrencyFormatter, useDateFormatter } from "hooks";
import i18n from "i18n/I18nApp";
import { Badge, Tooltip, TooltipEnums } from "kubra-ux-forge";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { getLabelsWithKrn } from "selectors/labelsWithKrn";
import { getPayableSession } from "selectors/payableSession";
import {
  getPaymentIntent,
  getPaymentMethodLabel,
  getSelectedPayableAmountOption,
} from "selectors/paymentIntent";
import { AccountPayable, InvoicePayable, OrderPayable } from "types/payable";
import {
  getBadgeStyle,
  getLabelWithKrn,
  mappingPaymentStatus,
  upperCaseFirstLetterInString,
} from "utilities/helper";
import { FeesAndTotal } from "../fees-and-total/fees-and-total";

interface PaymentSummaryProps {
  showDueDate?: boolean;
  showAmountDue?: boolean;
  conditionalPaymentMethod?: boolean;
  businessUnitDataCy?: string;
}

export const PaymentSummary = ({
  showDueDate = false,
  showAmountDue = false,
  conditionalPaymentMethod = false,
  businessUnitDataCy = "ez-pay-summary-field-item-freeform-field",
}: PaymentSummaryProps) => {
  const { t } = useTranslation(PAYABLE_REVIEW_NAMESPACE);
  const labelSourceWithKrn = useSelector(getLabelsWithKrn);

  const payableSession = useSelector(getPayableSession);
  const paymentIntentState = useSelector(getPaymentIntent);
  const selectedPayableType = payableSession?.payableType;
  const selectedPayable = payableSession?.payable;

  const isMultiBusinessUnitSupported =
    (selectedPayableType?.supportedBusinessUnitCodes?.length ?? 0) > 1;

  const selectedCharities = paymentIntentState.charities?.filter(
    (char) => char.selected,
  );

  const paymentMethodLabel = useSelector((state) =>
    getPaymentMethodLabel(state, t),
  );

  const dateFormatter = useDateFormatter();
  const currencyFormatter = useCurrencyFormatter();

  const selectedAmountOption = useSelector(getSelectedPayableAmountOption);

  // payableAmount is unset for multi-option payables, so the amount due is the selected option's amount.
  const amountDue =
    selectedAmountOption?.optionAmount ??
    payableSession?.payable?.payableAmount ??
    0;

  const category = selectedPayable?.category?.toLowerCase();
  // Invoices with multiple payment options already show the chosen amount, so Amount Due is redundant.
  const isInvoiceWithAmountOptions =
    category === "invoice" && !!selectedPayable?.payableAmountOptions;
  const showAmountDueRow = category !== "freeform" && !isInvoiceWithAmountOptions;

  if (!payableSession?.payableItemResponse) {
    throw new Error("Prepare endpoint response doesn't exist in the session");
  }

  if (!payableSession?.payable) {
    throw new Error("Payable not available in the session");
  }

  const paymentMethodContent = (
    <div
      className="ez-pay-summary-field-item"
      data-cy="ez-pay-summary-field-item-payment-method"
    >
      <dt data-cy="ez-pay-summary-field-item-payment-method-label">{t("payment-method")}</dt>
      <dd>
        {paymentIntentState.paymentMethod == FundingOption.ApplePay && (
          <img
            src={apple_pay_mark}
            alt="Apple Pay"
            style={{ height: "30px" }}
          ></img>
        )}
        {paymentIntentState.paymentMethod == FundingOption.GooglePay && (
          <img
            src={google_pay_mark}
            alt="Google Pay"
            style={{ height: "30px" }}
          ></img>
        )}
        {paymentIntentState.paymentMethod == FundingOption.PayPal && (
          <img src={paypal_mark} alt="PayPal"></img>
        )}
        <span data-cy="ez-pay-summary-field-item-payment-method-value">{paymentMethodLabel}</span>
      </dd>
    </div>
  );

  return (
    <dl data-cy="ez-pay-summary-container" className="ez-pay-summary-container">
      <div
        className="ez-pay-summary-field-item"
        data-cy="ez-pay-summary-field-item-item"
      >
        <dt data-cy="ez-pay-summary-field-item-item-label">{t("item")}</dt>
        <dd data-cy="ez-pay-summary-field-item-item-value">
          {getLabelWithKrn(labelSourceWithKrn.labelsWithKrn, selectedPayableType?.typeKrn ?? null, selectedPayableType?.name ?? "")}
        </dd>
      </div>

      {selectedPayableType?.category?.toLowerCase() === "order" &&
        (payableSession?.payable as OrderPayable)?.quantity != null &&
        typeof (payableSession?.payable as OrderPayable)?.quantity === "number" && (
          <div
            className="ez-pay-summary-field-item"
            data-cy="ez-pay-summary-field-item-quantity-number"
          >
            <dt data-cy="ez-pay-summary-field-item-quantity-number-label">
              {getLabelWithKrn(
                labelSourceWithKrn.labelsWithKrn,
                selectedPayableType?.typeKrn ?? null,
                "payableQuantity"
              )}
            </dt>
            <dd data-cy="ez-pay-summary-field-item-quantity-number-value">
              {getLabelWithKrn(
                labelSourceWithKrn.labelsWithKrn,
                selectedPayableType?.typeKrn ?? null,
                String((payableSession?.payable as OrderPayable).quantity)
              )}
            </dd>
          </div>
        )}

      {(selectedPayableType?.category?.toLowerCase() === "freeform" || selectedPayableType?.category?.toLowerCase() === "order") && isMultiBusinessUnitSupported && (
        <div
          className="ez-pay-summary-field-item"
          data-cy={businessUnitDataCy}
        >
          <dt data-cy="ez-pay-summary-field-item-business-unit-type-label">
            {getLabelWithKrn(labelSourceWithKrn.labelsWithKrn, selectedPayableType?.typeKrn ?? null, "businessUnitType")}
          </dt>
          <dd data-cy="ez-pay-summary-field-item-business-unit-type-value"> 
            {getLabelWithKrn(labelSourceWithKrn.labelsWithKrn, selectedPayableType?.typeKrn ?? null, payableSession?.payable?.businessUnitCode ?? "")}
          </dd>
        </div>
      )}

      {selectedPayableType?.category.toLowerCase() === "invoice" ? (
        <div
          className="ez-pay-summary-field-item"
          data-cy="ez-pay-summary-field-item-invoice-number"
        >
          <dt data-cy="ez-pay-summary-field-item-invoice-number-label">
            {getLabelWithKrn(labelSourceWithKrn.labelsWithKrn, selectedPayableType?.typeKrn ?? null, "invoiceNumber")}
          </dt>
          <dd data-cy="ez-pay-summary-field-item-invoice-number-value">
            {(selectedPayable as InvoicePayable).invoiceNumber}
          </dd>
        </div>
      ) : null}

      {selectedPayableType?.category.toLowerCase() === "account" ? (
        <div
          className="ez-pay-summary-field-item"
          data-cy="ez-pay-summary-field-item-account-number"
        >
          <dt  data-cy="ez-pay-summary-field-item-account-number-label">
            {getLabelWithKrn(labelSourceWithKrn.labelsWithKrn, selectedPayableType?.typeKrn ?? null, "accountNumber")}
          </dt>
          <dd data-cy="ez-pay-summary-field-item-account-number-value">
            {(selectedPayable as AccountPayable).accountNumber}
          </dd>
        </div>
      ) : null}

      {paymentIntentState.paymentId && (
        <div
          className="ez-pay-summary-field-item"
          data-cy="ez-pay-summary-field-item-payment-id"
        >
          <dt data-cy="ez-pay-summary-field-item-payment-id-label">
            {t("confirmation-number")}
          </dt>
          <dd data-cy="ez-pay-summary-field-item-payment-id-value">
            {paymentIntentState.paymentId}
          </dd>
        </div>
      )}

      {paymentIntentState.paymentStatus && (
        <div
          className="ez-pay-summary-field-item"
          data-cy="ez-pay-summary-field-item-payment-status"
        >
          <dt data-cy="ez-pay-summary-field-item-payment-status-label">{t("status")}</dt>
          <Badge
            text={upperCaseFirstLetterInString(
              t(mappingPaymentStatus(paymentIntentState.paymentStatus), {
                ns: COMMON_NAMESPACE,
              })
            )}
            variant={getBadgeStyle(paymentIntentState.paymentStatus)}
          />
        </div>
      )}

      <div
        className="ez-pay-summary-field-item"
        data-cy="ez-pay-summary-field-item-payment-date"
      >
        <dt data-cy="ez-pay-summary-field-item-payment-date-label">{t("payment-date")}</dt>
        <dd data-cy="ez-pay-summary-field-item-payment-date-value"> 
          {dateFormatter.shortIsoFormat(paymentIntentState.paymentDate)}
        </dd>
      </div>

      {showDueDate && payableSession?.payable?.dueDate && (
        <div
          className="ez-pay-summary-field-item"
          data-cy="ez-pay-summary-field-item-due-date"
        >
          <dt data-cy="ez-pay-summary-field-item-due-date-label">{t("due-date")}</dt>
          <dd data-cy="ez-pay-summary-field-item-due-date-value">
            {payableSession?.payable?.dueDate
              ? new Intl.DateTimeFormat(i18n.language, {
                timeZone: "UTC",
                year: "numeric",
                month: "short",
                day: "numeric",
              }).format(new Date(payableSession?.payable?.dueDate))
              : ""}
          </dd>
        </div>
      )}

      {conditionalPaymentMethod
        ? paymentIntentState.paymentMethod && paymentMethodContent
        : paymentMethodContent
      }

      <div
        className="ez-pay-summary-field-item"
        data-cy="ez-pay-summary-field-item-payment-amount"
      >
        <dt data-cy="ez-pay-summary-field-item-payment-amount-label">
          {t("payment-amount")}
          {selectedAmountOption && (
            <>
              &nbsp;
              <Tooltip
                direction={TooltipEnums.TopRight}
                content={getLabelWithKrn(
                  labelSourceWithKrn.labelsWithKrn,
                  selectedPayableType?.typeKrn ?? null,
                  selectedAmountOption.labelKey,
                )}
                data-cy="payment-amount-tooltip"
                variant="rich"
              />
            </>
          )}
        </dt>
        <dd data-cy="ez-pay-summary-field-item-payment-amount-value">
          {currencyFormatter.format(
            payableSession?.payableItemResponse?.paymentAmount ?? 0
          )}
        </dd>
      </div>

      {showAmountDue && showAmountDueRow && (
        <div
          className="ez-pay-summary-field-item"
          data-cy="ez-pay-summary-field-item-amount-due"
        >
          <dt data-cy="ez-pay-summary-field-item-amount-due-label">{t("amount-due")}</dt>
          <dd data-cy="ez-pay-summary-field-item-amount-due-value">
            {currencyFormatter.format(amountDue)}
          </dd>
        </div>
      )}

      <FeesAndTotal
        selectedCharities={selectedCharities}
        payableSession={payableSession}
        currencyFormatter={currencyFormatter}
      />
    </dl>
  );
};
